/******************************************************************************************
 *	License Summary Utility Methods
 *******************************************************************************************/
(function() {


  angular.module('rcServices.licenseSummaryUtility', ['binky.core.product']).service(
    'LicenseSummaryUtility',
    /* @ngInject */
    function(_, feature, LICENSE_TYPE) {
      var self = this;
      var licenseSummarySectionHasError = function(summarySectionData) {
        var hasError = false;
        _.forEach(summarySectionData, function(value, key) {
          // Update the value to be a number
          summarySectionData[key] = Number(value);
          // TO DO: determine what marks an error. Currently we don't know if the data will have any such marker
          // (we thought it'd be negative numbers, but it turns out it's really hard to find out when there's an error)
          // So for now, all this method does is cast the value to an integer; hopefully someday we can actually
          // check for an error. If not, this method can go away (the casting can either move elsewhere or be unneeded).
        });
        return hasError;
      };

      /**
       * @description Parse through the license summary to find out if we had errors getting the data,
       * totals of all the kinds of licenses, etc, and add the info to the summary.
       *
       * Can be called on the delegationSummary from the customer level (customer list) or from the
       * product/enterpriseProduct level (customer detail)
       *
       * @param {Object} delegationSummary Object containing summary information
       *  that will be totalled.
       * @param {Array<Product>} products Array of products from products API
       *  response.
       * */
      this.rollUpSummaryData = function(delegationSummary, products) {
        var totalLicenses = 0;
        var totalOveractivatedLicenses = 0;
        var totalPaidLicenses = 0;
        var totalUnpaidLicenses = 0;
        var totalActiveLicenses = 0;
        var totalAvailableLicenses = 0;
        var licenseCountErrorState = false;
        var hasNamedLicenses = false;
        _.forEach(delegationSummary, function(data, section) {
          if (data) {
            licenseCountErrorState = licenseCountErrorState || licenseSummarySectionHasError(data);
            if (!licenseCountErrorState && section !== 'totalSummary') {
              // no point rolling up the data if we're in an error state
              // totalLicenses is built from paid and pending; the other statuses all would be duplicates after that.
              if (isFinite(data.paidSeatCount)) {
                totalPaidLicenses += Number(data.paidSeatCount);
                totalLicenses += Number(data.paidSeatCount);
              }
              if (isFinite(data.unpaidSeatCount)) {
                totalUnpaidLicenses += Number(data.unpaidSeatCount);
                totalLicenses += Number(data.unpaidSeatCount);
              }
              if (isFinite(data.activeSeatCount)) {
                totalActiveLicenses += Number(data.activeSeatCount);
              }
              if (isFinite(data.availableSeatCount)) {
                totalAvailableLicenses += Math.max(Number(data.availableSeatCount), 0);
              }
              if (isFinite(data.overActivatedSeatCount)) {
                totalOveractivatedLicenses += Number(data.overActivatedSeatCount);
              }
            }
          }
        });

        // The customer has named licenses if the count of either paid or unpaid is greater than 0
        if (
          delegationSummary.namedSummary &&
          (delegationSummary.namedSummary.paidSeatCount ||
            delegationSummary.namedSummary.unpaidSeatCount)
        ) {
          hasNamedLicenses = true;
        }
        delegationSummary.licenseCountError = licenseCountErrorState;
        delegationSummary.hasNamedLicenses = hasNamedLicenses;
        delegationSummary.hasOveractivatedLicenses = totalOveractivatedLicenses > 0;
        if (!delegationSummary.totalSummary) {
          delegationSummary.totalSummary = {
            paidSeatCount: totalPaidLicenses,
            unpaidSeatCount: totalUnpaidLicenses,
            activeSeatCount: totalActiveLicenses,
            availableSeatCount: totalAvailableLicenses,
            overActivatedSeatCount: totalOveractivatedLicenses,
            totalSeatCount: totalLicenses
          };
        }
        if (
          delegationSummary.totalSummary &&
          delegationSummary.totalSummary.availableSeatCount &&
          delegationSummary.totalSummary.availableSeatCount < 0
        ) {
          delegationSummary.totalSummary.availableSeatCount = 0;
        }

        if (!_.isNil(products)) {
          const totalPaidSeatCount = _.sumBy(products, product =>
            _.invoke(product, 'licenseQuantityList.getPaidOrRenewingQuantity')
          );
          const totalActiveSeatCount = _.sumBy(products, product =>
            _.get(product, 'provisionedQuantity', 0)
          );
          const totalUnpaidSeatCount = _.sumBy(products, product =>
            _.invoke(
              product,
              'licenseQuantityList.getPendingPaymentOrGracePastDueOrPastDueQuantity'
            )
          );
          const totalSeatCount = totalPaidSeatCount + totalUnpaidSeatCount;

          delegationSummary.totalSummary.paidSeatCount = totalPaidSeatCount;
          delegationSummary.totalSummary.activeSeatCount = totalActiveSeatCount;
          delegationSummary.totalSummary.unpaidSeatCount = totalUnpaidSeatCount;
          delegationSummary.totalSummary.totalSeatCount = totalSeatCount;
          delegationSummary.totalSummary.availableSeatCount = Math.max(
            totalSeatCount - totalActiveSeatCount,
            0
          );
          delegationSummary.totalSummary.overActivatedSeatCount = Math.max(
            totalActiveSeatCount - totalSeatCount,
            0
          );
        }

        return delegationSummary;
      };

      /*
         * Add rollup metadata to the summary if it exists, or create an empty summary (with error state) if it doesn't.
         * For use at the customer level (usually from the customer list).
         * */
      this.createLicenseSummaryMetadata = function(customer) {
        if (customer.delegationSummary) {
          customer.delegationSummary = self.rollUpSummaryData(customer.delegationSummary);
        } else {
          // We have no summary, show the error state
          customer.delegationSummary = {licenseCountError: true};
        }
      };

      /**
       * @description Adds any missing fields to product license summary data, with values of 0.
       *  Also adds totalSeatCount (sum of paid and unpaid) to the summary
       *
       * @param {CustomerDetailsProduct} product product that was included in
       *  the customer details API response.
       * @param {Product} binkyProduct product coming from the product list API
       *  response.
       * */
      this.fixUpLicenseSummaryData = function(product, binkyProduct) {
        // add values (0) for any undefined summary items
        if (!isFinite(product.paidSeatCount)) {
          product.paidSeatCount = 0;
        }
        if (!isFinite(product.unpaidSeatCount)) {
          product.unpaidSeatCount = 0;
        }
        if (!isFinite(product.activeSeatCount)) {
          product.activeSeatCount = 0;
        }
        if (!isFinite(product.availableSeatCount) || product.availableSeatCount < 0) {
          product.availableSeatCount = 0;
        }
        if (!isFinite(product.overActivatedSeatCount)) {
          product.overActivatedSeatCount = 0;
        }
        if (!isFinite(product.totalSeatCount)) {
          product.totalSeatCount = Number(product.paidSeatCount) + Number(product.unpaidSeatCount);
        }
        product.licenseLanguageDetailVisible = false;

        if (
          !_.isNil(binkyProduct) &&
          binkyProduct.licenseType !== LICENSE_TYPE.MACHINE
        ) {
          // For parity with what the customer response API is doing, only count LQs that are PAID or renewing.
          product.paidSeatCount = _.invoke(
            binkyProduct,
            'licenseQuantityList.getPaidOrRenewingQuantity'
          );
          product.activeSeatCount = _.get(binkyProduct, 'provisionedQuantity', 0);
          product.unpaidSeatCount = _.invoke(
            binkyProduct,
            'licenseQuantityList.getPendingPaymentOrGracePastDueOrPastDueQuantity'
          );
          product.totalSeatCount = product.paidSeatCount + product.unpaidSeatCount;
          product.availableSeatCount = Math.max(
            product.totalSeatCount - product.activeSeatCount,
            0
          );
          product.overActivatedSeatCount = Math.max(
            product.activeSeatCount - product.totalSeatCount,
            0
          );
        }
      };

      var getSummaryForProductList = function(productList) {
        var totalOveractivatedLicenses = 0;
        var totalPaidLicenses = 0;
        var totalUnpaidLicenses = 0;
        var totalActiveLicenses = 0;
        var totalAvailableLicenses = 0;
        _.forEach(productList, function(product) {
          totalPaidLicenses += parseInt(product.paidSeatCount, 10);
          totalUnpaidLicenses += parseInt(product.unpaidSeatCount, 10);
          totalActiveLicenses += parseInt(product.activeSeatCount, 10);
          totalAvailableLicenses += parseInt(product.availableSeatCount, 10);
          totalOveractivatedLicenses += parseInt(product.overActivatedSeatCount, 10);
        });
        return {
          paidSeatCount: totalPaidLicenses,
          unpaidSeatCount: totalUnpaidLicenses,
          activeSeatCount: totalActiveLicenses,
          availableSeatCount: totalAvailableLicenses,
          overActivatedSeatCount: totalOveractivatedLicenses,
          totalSeatCount: totalPaidLicenses + totalUnpaidLicenses
        };
      };

      /*
        * Adds up totals based on the actual products data, excluding any consumable licenses
        * */
      this.calculateTotalSummaryFromProductsData = function(products) {
        var userLicensedSummary = getSummaryForProductList(products.userLicensedList);
        var deviceLicensedSummary = getSummaryForProductList(products.deviceLicensedList);

        return {
          paidSeatCount: _.sumBy([userLicensedSummary, deviceLicensedSummary], 'paidSeatCount'),
          unpaidSeatCount: _.sumBy([userLicensedSummary, deviceLicensedSummary], 'unpaidSeatCount'),
          activeSeatCount: _.sumBy([userLicensedSummary, deviceLicensedSummary], 'activeSeatCount'),
          availableSeatCount: _.sumBy(
            [userLicensedSummary, deviceLicensedSummary],
            'availableSeatCount'
          ),
          overActivatedSeatCount: _.sumBy(
            [userLicensedSummary, deviceLicensedSummary],
            'overActivatedSeatCount'
          ),
          totalSeatCount: _.sumBy([userLicensedSummary, deviceLicensedSummary], 'totalSeatCount')
        };
      };
    }
  );
})();
